import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { AppTable, AppInput, ChevronRightIcon } from '@pro-sto/ui';
import type { AppTableColumn } from '@pro-sto/ui';
import { clientsStore } from '@/stores/ClientsStore';
import type { ClientItem } from '@/stores/ClientsStore';
import { ROUTES } from '@/constants/routes';
import './ClientsPage.css';

function formatPhone(phone: unknown): string {
  const raw = typeof phone === 'string' ? phone : String(phone ?? '');
  const digits = raw.replace(/\D/g, '');

  if (digits.length === 11) {
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  }
  if (digits.length === 10) {
    return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
  }
  return raw || '—';
}

const ClientsPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    clientsStore.loadClients();
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !clientsStore.isLoadingMore && clientsStore.hasMore) {
          clientsStore.loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      clientsStore.setSearch(value);
    }, 400);
  };

  const columns: AppTableColumn<ClientItem>[] = [
    {
      label: 'Телефон',
      width: '35%',
      render: (row) => formatPhone(row.phone),
    },
    {
      label: 'Имя',
      width: '45%',
      render: (row) => (row.name as unknown as string) || '—',
    },
    {
      label: '',
      width: '20%',
      align: 'right',
      render: (row) => (
        <button
          className="clients-page__detail-btn"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${ROUTES.CLIENTS}/${row.uuid}`);
          }}
          title="Подробнее"
          type="button"
        >
          <ChevronRightIcon size={20} />
        </button>
      ),
    },
  ];

  return (
    <div className="clients-page" data-testid="clients-page">
      <div className="clients-page__header">
        <h2 className="clients-page__title">Клиенты</h2>
        <div className="clients-page__search">
          <AppInput
            value={searchValue}
            onChange={handleSearch}
            placeholder="Поиск по телефону или имени"
            size="M"
            background="muted"
          />
        </div>
      </div>

      <div className="clients-page__table-wrap">
        {clientsStore.isLoading ? (
          <div className="clients-page__loading">Загрузка...</div>
        ) : (
          <>
            <AppTable<ClientItem>
              columns={columns}
              rows={clientsStore.clients}
              getRowKey={(row) => row.uuid}
              emptyPlaceholder="Нет клиентов"
            />
            <div ref={loadMoreRef} className="clients-page__load-trigger" />
            {clientsStore.isLoadingMore && (
              <div className="clients-page__loading-more">
                <div className="clients-page__spinner" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default ClientsPage;
